import { OptionsType } from '@openx/types/options.ts';
import type { PackageProviderConfig } from '@openx/types/package/packageProviderConfig.ts';
import { noop } from '@openx/utils/noop.ts';
import { useDeviceTypesOptionsFetch } from '../targeting/utils/useDeviceTypesOptionsFetch.ts';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch.ts';
import { useCloneForm } from './utils/useCloneForm.ts';
import { useCreateForm } from './utils/useCreateForm.ts';
import { useDealDisableRbo } from './utils/useDealDisableRbo.ts';
import { useDealsFetch } from './utils/useDealsFetch.ts';
import { useDetailsConfig } from './utils/useDetailsConfig.ts';
import { useDispatchGetNetworkAndPublishersAccountsOptions } from './utils/useDispatchGetNetworkAndPublishersAccountsOptions.ts';
import { useDistributionChannelOptionsFetch } from './utils/useDistributionChannelOptions.ts';
import { useOldDistributionChannelOptionsFetch } from './utils/useOldDistributionChannelOptions.ts';
import { usePackageClone } from './utils/usePackageClone.ts';
import { usePackageCreate } from './utils/usePackageCreate.ts';
import { usePackageFetch, usePackageLazyFetch } from './utils/usePackageFetch.ts';
import { usePackageOperationActions } from './utils/usePackageOperationActions.ts';
import { usePackagePermissions } from './utils/usePackagePermissions.ts';
import { usePackageRetargetingDealsValidation } from './utils/usePackageRetargetingDealsValidation.ts';
import { usePackagesListFetch } from './utils/usePackagesListFetch.ts';
import { usePackageUpdate } from './utils/usePackageUpdate.ts';
import { usePackageUsedBy } from './utils/usePackageUsedBy.ts';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch.ts';
import { useTargetablePackagesFetch } from './utils/useTargetablePackagesFetch.ts';
import { useTechnologyOptionsFetch } from './utils/useTechnologyOptionsFetch.ts';
import { useUpdateForm } from './utils/useUpdateForm.ts';

const todoConfig = {
  useFloorsFetch: () => {
    throw new Error('useFloorsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
};

export const packageConfig: PackageProviderConfig = {
  ...todoConfig,
  isOa: true,
  isOaSuborg: false,
  useAccountOptionsFetch,
  useCloneForm,
  useCreateForm,
  useDealsFetch,
  useDetailsConfig,
  useDispatchGetNetworkAndPublishersAccountsOptions,
  useDeviceTypesOptionsFetch: () => {
    const deviceTypesOptions = useDeviceTypesOptionsFetch();

    return {
      [OptionsType.DESKTOP_DEVICES]: {
        data: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.MOBILE_DEVICES]: {
        data: deviceTypesOptions[OptionsType.MOBILE_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.MOBILE_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.MOBILE_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.TV_DEVICES]: {
        data: deviceTypesOptions[OptionsType.TV_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.TV_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.TV_DEVICES].refetch || (() => Promise.resolve()),
      },
    };
  },
  useDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useOldDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useOldDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  usePackageClone,
  usePackageCreate,
  usePackageUpdate,
  usePackageFetch,
  usePackageOperationActions,
  usePackagePermissions,
  usePackageUsedBy,
  usePackagesListFetch,
  useRelatedAccountsFetch,
  useTargetablePackagesFetch,
  useTechnologyOptionsFetch: (type, isUsed) => {
    const {
      options,
      isLoading,
      refetch = () => {
        noop();
      },
    } = useTechnologyOptionsFetch(type, isUsed);

    return {
      deviceTypeOptions: options,
      isLoading,
      refetch,
    };
  },
  useUpdateForm,
  usePackageLazyFetch,
  useDealDisableRbo,
  usePackageRetargetingDealsValidation,
};
